import { FC, useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { IntercomProvider } from '@cld/intercom';
import { SomethingWentWrong } from '@cld/empty-state';
import { isForbiddenCustomer, isInvalidCustomer } from '@cld/session-service';
import { useAccountData } from './useAccountData';
import { ApplicationWrapperContextProvider } from './context/applicationWrapper/ApplicationWrapper.context';
import { ApplicationDropdownWrapperContextProvider } from './context/applicationDropdownWrapper/ApplicationsDropdownWrapper.context';
import { WorkspaceMenuContextProvider } from './context/workspaceMenu/WorkspaceMenu.context';
import { SideMenuProvider } from './components';
import { useGetCustomers } from './api/customers/useGetCustomers';
import { PrivacyProvider, useCustomerIdContext, useUserData } from './context';
import { FetchStatus } from './api/api.types';
import { ApplicationWrapperResponsive } from './ApplicationWrapper';
import { ApplicationWrapperConnectedProps } from './ApplicationWrapper.types';
import { CustomerNotFound } from './components/CustomerNotFound/CustomerNotFound';
import { ProgressSpinner } from './components/ProgressSpinner/ProgressSpinner';
import { getUserDataAsIntercomUser } from './api/api.utils';

export const ApplicationWrapperConnected: FC<ApplicationWrapperConnectedProps> = ({
  children,
  selectedAppId,
  onAppSelect,
  logOut,
  onError,
  isMobile = false,
  primaryMenu,
  sideBarMenu,
  intercomSettings,
  header,
  unicornApi,
}) => {
  const { getCustomersList, customersList, fetchCustomersStatus } = useGetCustomers(selectedAppId);
  const { currentCustomer } = useCustomerIdContext();
  const { userData } = useUserData();
  const intercomUser = getUserDataAsIntercomUser(userData);
  const [showCustomerAssets] = useState(false);
  const accountData = useAccountData({ customerExternalId: currentCustomer?.externalId, unicornApi });
  const companyInfo = {
    company: {
      name: accountData?.companyName ?? accountData?.name ?? 'N/A',
      id: accountData?.externalId,
    },
  };

  const applicationData = useMemo(
    () => ({
      selectedAppId,
      isMobile,
      logOut,
    }),
    [selectedAppId, isMobile, logOut]
  );

  const getAppContent = () => {
    if (fetchCustomersStatus === FetchStatus.Failure) {
      return <SomethingWentWrong isMobile={isMobile} />;
    }
    if (isInvalidCustomer(currentCustomer) || isForbiddenCustomer(currentCustomer)) {
      return <CustomerNotFound isMobile={isMobile} />;
    }

    const content = currentCustomer && userData ? children : <ProgressSpinner />;

    return (
      <ApplicationWrapperResponsive
        header={header}
        primaryMenu={primaryMenu}
        sideBarMenu={sideBarMenu}
        isMobile={isMobile}
        children={content}
        unicornApi={unicornApi}
      />
    );
  };

  const onErrorCallback = useCallback(
    (error: Error) => {
      console.error(error.message);
      onError?.(error);
    },
    [onError]
  );

  return (
    <ErrorBoundary fallback={<SomethingWentWrong isMobile={isMobile} />} onError={onErrorCallback}>
      <ApplicationWrapperContextProvider value={applicationData}>
        <PrivacyProvider showCustomerAssets={showCustomerAssets}>
          <SideMenuProvider>
            <IntercomProvider
              settings={intercomSettings}
              isShown={!isMobile}
              user={intercomUser}
              cloudName={currentCustomer?.cloudName}
              companyInfo={companyInfo}
            >
              <ApplicationDropdownWrapperContextProvider onAppSelect={onAppSelect}>
                <WorkspaceMenuContextProvider getCustomersList={getCustomersList} customersList={customersList}>
                  {getAppContent()}
                </WorkspaceMenuContextProvider>
              </ApplicationDropdownWrapperContextProvider>
            </IntercomProvider>
          </SideMenuProvider>
        </PrivacyProvider>
      </ApplicationWrapperContextProvider>
    </ErrorBoundary>
  );
};
